/**
 * Audio engine — owns all five 2A03 channel nodes and the master scheduler.
 *
 * Phase 1 step 4: plays a hardcoded demo song from `demo-song.ts`. Phase
 * 1 step 5 hooks this up to the Project data model from @pulsewright/shared
 * so the tracker grid can drive playback.
 */

import {
  createDPCMNode,
  createNoiseNode,
  createPulseNode,
  createTriangleNode,
  generateKick,
  generateSnare,
  loadDPCMWorklet,
  loadNoiseWorklet,
  loadPulseWorklet,
  loadTriangleWorklet,
  noteToFrequency,
  type DPCMNode,
  type NoiseNode,
  type PulseDuty,
  type PulseNode,
  type TriangleNode,
} from './';
import { getAudioContext } from './context';
import type { DemoRow, DemoSong } from './demo-song';
import { createScheduler, type Scheduler, type StartPosition } from './scheduler';
import { rowDurationSec } from './tempo';

export type PreviewChannel = 'pulse1' | 'pulse2' | 'triangle' | 'noise';

export interface PlayOptions {
  /** Order-list index to begin playback at. Defaults to 0. */
  fromOrderIndex?: number;
  /** Row within that pattern to begin at. Defaults to 0. */
  fromRow?: number;
}

export interface EngineHandle {
  playDemo(song: DemoSong, speed: number, tempo: number, opts?: PlayOptions): Promise<void>;
  stop(): void;
  pause(): void;
  resume(): void;
  isPlaying(): boolean;
  /** Returns the current playhead position, or null when stopped. */
  getPlayhead(): { orderIndex: number; rowInPattern: number } | null;
  /** Update tempo (and optionally speed) live, with or without active playback. */
  setTempo(speed: number, tempo: number): void;
  /**
   * Replace the engine's live song reference. Cell content changes are
   * reflected on the next row that fires. Order-list / loop-point changes
   * are NOT — they still need a stop+play cycle.
   */
  updateSong(song: DemoSong): void;
  /**
   * Briefly trigger a note for ear-confirmation when the user types in the
   * grid. Independent of song playback. ~150 ms default; held notes already
   * playing on the channel will be replaced.
   */
  preview(channel: PreviewChannel, note: string, durationMs?: number): Promise<void>;
  destroy(): void;
}

interface Channels {
  pulse1: PulseNode;
  pulse2: PulseNode;
  triangle: TriangleNode;
  noise: NoiseNode;
  dpcm: DPCMNode;
}

/** Lazily prepare all channels + load the DPCM samples. Idempotent. */
async function prepareChannels(ctx: AudioContext, existing: Channels | null): Promise<Channels> {
  if (existing) return existing;

  await Promise.all([
    loadPulseWorklet(ctx),
    loadTriangleWorklet(ctx),
    loadNoiseWorklet(ctx),
    loadDPCMWorklet(ctx),
  ]);

  const pulse1 = createPulseNode(ctx);
  const pulse2 = createPulseNode(ctx);
  const triangle = createTriangleNode(ctx);
  const noise = createNoiseNode(ctx);
  const dpcm = createDPCMNode(ctx);

  // Load default drum samples.
  const kick = generateKick();
  const snare = generateSnare();
  dpcm.loadSample('kick', kick.data, kick.sampleRate);
  dpcm.loadSample('snare', snare.data, snare.sampleRate);

  return { pulse1, pulse2, triangle, noise, dpcm };
}

/** Apply a single demo row to the live channels. */
function dispatchDemoRow(channels: Channels, row: DemoRow): void {
  // Re-attack envelope on every dispatched note so back-to-back notes of
  // the same pitch read as two distinct hits. 8 ms gives a clearly-audible
  // separation without sounding "fluttery" on sustained melodies.
  const ATTACK_MS = 8;
  if (row.pulse1) {
    const cell = row.pulse1;
    if (cell.note === 'OFF') {
      channels.pulse1.noteOff();
    } else if (cell.note) {
      channels.pulse1.setFrequency(noteToFrequency(cell.note));
      if (cell.duty !== undefined) channels.pulse1.setDuty(cell.duty as PulseDuty);
      if (cell.volume !== undefined) channels.pulse1.setVolume(cell.volume);
      channels.pulse1.noteOn(ATTACK_MS);
    }
  }
  if (row.pulse2) {
    const cell = row.pulse2;
    if (cell.note === 'OFF') channels.pulse2.noteOff();
    else if (cell.note) {
      channels.pulse2.setFrequency(noteToFrequency(cell.note));
      if (cell.duty !== undefined) channels.pulse2.setDuty(cell.duty as PulseDuty);
      if (cell.volume !== undefined) channels.pulse2.setVolume(cell.volume);
      channels.pulse2.noteOn(ATTACK_MS);
    }
  }
  if (row.triangle) {
    const cell = row.triangle;
    if (cell.note === 'OFF') channels.triangle.noteOff();
    else if (cell.note) {
      channels.triangle.setFrequency(noteToFrequency(cell.note));
      channels.triangle.noteOn(ATTACK_MS);
    }
  }
  if (row.noise) {
    const cell = row.noise;
    if (cell.note === 'OFF') {
      channels.noise.noteOff();
    } else if (cell.note) {
      if (cell.periodIndex !== undefined) channels.noise.setPeriodIndex(cell.periodIndex);
      if (cell.mode !== undefined) channels.noise.setMode(cell.mode);
      if (cell.volume !== undefined) channels.noise.setVolume(cell.volume);
      // Default to a short percussive decay so dense hat patterns don't smear.
      // The cell can override via `decayMs` (e.g. open hats with longer rings).
      channels.noise.noteOn(cell.decayMs ?? 55);
    }
  }
  if (row.dpcm) {
    const cell = row.dpcm;
    if (cell.trigger) {
      if (cell.rateIndex !== undefined) channels.dpcm.setRateIndex(cell.rateIndex);
      if (cell.volume !== undefined) channels.dpcm.setVolume(cell.volume);
      channels.dpcm.trigger(cell.trigger, false);
    }
  }
}

/** Stop every channel cleanly. */
function silence(channels: Channels): void {
  channels.pulse1.noteOff();
  channels.pulse2.noteOff();
  channels.triangle.noteOff();
  channels.noise.noteOff();
  channels.dpcm.stop();
}

const DEFAULT_PREVIEW_MS = 150;

export function createEngine(): EngineHandle {
  let channels: Channels | null = null;
  let scheduler: Scheduler | null = null;
  let previewTimers: Map<PreviewChannel, ReturnType<typeof setTimeout>> = new Map();
  /** Latest known song. Mutated by updateSong; read by onRow. */
  let liveSong: DemoSong | null = null;
  /**
   * Pending audio dispatches — scheduler emits onRow with look-ahead, we
   * setTimeout the actual `dispatchDemoRow` call to fire at the row's
   * intended audioTime so audio aligns with the visual playhead.
   */
  let pendingDispatches: ReturnType<typeof setTimeout>[] = [];
  /** Position of the row whose audio is currently sounding (last dispatched). */
  let currentlyPlayingRow: { orderIndex: number; rowInPattern: number } | null = null;

  function clearPendingDispatches(): void {
    for (const t of pendingDispatches) clearTimeout(t);
    pendingDispatches = [];
  }

  async function playDemo(
    song: DemoSong,
    speed: number,
    tempo: number,
    opts?: PlayOptions,
  ): Promise<void> {
    const ctx = await getAudioContext();
    channels = await prepareChannels(ctx, channels);
    scheduler?.stop();
    clearPendingDispatches();
    currentlyPlayingRow = null;
    silence(channels);

    liveSong = song;
    const rowDur = rowDurationSec(speed, tempo);
    scheduler = createScheduler({
      clock: ctx,
      rowDurationSec: rowDur,
      patternRowCount: song.rowsPerPattern,
      orderList: song.orderList,
      loopStartOrderIndex: song.loopStartOrderIndex,
      onRow: (event) => {
        if (!channels || !liveSong) return;
        // Scheduler fires events up to lookAhead seconds ahead of their
        // audioTime. Defer the actual dispatch with setTimeout so the audio
        // (and the playhead state we update at the same instant) lines up
        // with the row's musical position.
        const delaySec = Math.max(0, event.audioTime - ctx.currentTime);
        const delayMs = delaySec * 1000;
        const orderIndex = event.orderIndex;
        const rowInPattern = event.rowInPattern;
        const patternIndex = event.patternIndex;
        const fire = () => {
          if (!channels || !liveSong) return;
          const pattern = liveSong.patterns[patternIndex];
          if (!pattern) return;
          const row = pattern[rowInPattern];
          if (!row) return;
          dispatchDemoRow(channels, row);
          currentlyPlayingRow = { orderIndex, rowInPattern };
        };
        if (delayMs < 1) {
          fire();
        } else {
          const handle = setTimeout(fire, delayMs);
          pendingDispatches.push(handle);
        }
      },
    });

    const startPos: StartPosition = {
      orderIndex: opts?.fromOrderIndex ?? 0,
      rowInPattern: opts?.fromRow ?? 0,
    };
    scheduler.start(startPos);
  }

  return {
    playDemo,
    stop() {
      scheduler?.stop();
      scheduler = null;
      clearPendingDispatches();
      currentlyPlayingRow = null;
      if (channels) silence(channels);
    },
    pause() {
      scheduler?.pause();
      clearPendingDispatches();
      currentlyPlayingRow = null;
      if (channels) silence(channels);
    },
    resume() {
      scheduler?.resume();
    },
    isPlaying: () => scheduler?.isPlaying() ?? false,
    /**
     * Returns the row whose audio is currently sounding — i.e. the row we
     * actually dispatched, NOT the scheduler's look-ahead cursor (which
     * runs ~100 ms ahead). Visual playhead matches what you're hearing.
     */
    getPlayhead: () => currentlyPlayingRow,
    setTempo(speed: number, tempo: number) {
      const dur = rowDurationSec(speed, tempo);
      scheduler?.setRowDurationSec(dur);
    },
    updateSong(song: DemoSong) {
      liveSong = song;
    },
    async preview(channel: PreviewChannel, note: string, durationMs = DEFAULT_PREVIEW_MS) {
      const ctx = await getAudioContext();
      channels = await prepareChannels(ctx, channels);

      // Cancel any pending preview-off on this channel.
      const existing = previewTimers.get(channel);
      if (existing) clearTimeout(existing);

      if (channel === 'pulse1' || channel === 'pulse2') {
        const node = channels[channel];
        node.setFrequency(noteToFrequency(note));
        node.setDuty(1);
        node.setVolume(12);
        node.noteOn();
        previewTimers.set(
          channel,
          setTimeout(() => {
            node.noteOff();
            previewTimers.delete(channel);
          }, durationMs),
        );
      } else if (channel === 'triangle') {
        channels.triangle.setFrequency(noteToFrequency(note));
        channels.triangle.noteOn();
        previewTimers.set(
          'triangle',
          setTimeout(() => {
            channels?.triangle.noteOff();
            previewTimers.delete('triangle');
          }, durationMs),
        );
      } else if (channel === 'noise') {
        channels.noise.setPeriodIndex(5);
        channels.noise.setMode('long');
        channels.noise.setVolume(10);
        channels.noise.noteOn();
        previewTimers.set(
          'noise',
          setTimeout(() => {
            channels?.noise.noteOff();
            previewTimers.delete('noise');
          }, durationMs),
        );
      }
    },
    destroy() {
      scheduler?.stop();
      scheduler = null;
      clearPendingDispatches();
      currentlyPlayingRow = null;
      for (const t of previewTimers.values()) clearTimeout(t);
      previewTimers.clear();
      if (channels) {
        channels.pulse1.destroy();
        channels.pulse2.destroy();
        channels.triangle.destroy();
        channels.noise.destroy();
        channels.dpcm.destroy();
        channels = null;
      }
    },
  };
}
